import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Maximize2, 
  Code, 
  Image, 
  BarChart, 
  ArrowRight, 
  X,
  Grid,
  Sparkles
} from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import demoSamples from '../data/demoSamples';

const Demo = () => {
  const [activeModalId, setActiveModalId] = useState(null);

  const openModal = (id) => {
    setActiveModalId(id);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setActiveModalId(null);
    document.body.style.overflow = 'auto';
  };

const getSampleIcon = (id) => {
  switch(id) {
    case 'movie-script':
      return <Code className="w-6 h-6 text-purple-500" />;
    case 'modern-architecture':
      return <Image className="w-6 h-6 text-blue-500" />;
    case 'crop-tracker':
      return <BarChart className="w-6 h-6 text-green-600" />;
    case 'admin-dashboard':
      return <BarChart className="w-6 h-6 text-rose-600" />;
    case 'horse-stables':
      return <Grid className="w-6 h-6 text-amber-600" />;
    default:
      return null;
  }
};

  return (
    <div className="font-sans antialiased text-gray-900">
      <MainNavbar activePage="demo" />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-white to-gray-100 text-slate-900">
        <div className="max-w-5xl mx-auto px-4 text-center">

<div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white px-5 py-2 rounded-full text-base font-bold tracking-wide mb-4 shadow-lg">
  <Sparkles className="w-4 h-4" />
  See What You Can Create
</div>
          <h1 className="text-4xl font-bold mb-6">Interactive Examples Created with Dessino.ai</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            From detailed dashboards to interactive applications, see how our AI transforms 
            ideas into fully functional designs.
          </p>
        </div>
      </section>

      {/* Demo Examples Section */}
      <section className="py-16 px-4 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 gap-16">
          {demoSamples.map((sample) => (
            <div key={sample.id} className="rounded-xl overflow-hidden shadow-md border border-gray-100">
              <div className="bg-white p-6 md:p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {getSampleIcon(sample.id)}
                    <h2 className="text-2xl font-bold text-slate-800">{sample.title}</h2>
                  </div>
                  <button 
                    onClick={() => openModal(sample.id)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium transition"
                  >
                    <Maximize2 className="w-4 h-4" />
                    <span>Fullscreen</span>
                  </button>
                </div>
                
                <p className="text-lg text-slate-700 mb-6">{sample.description}</p>
                
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">Key Features:</h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                    {sample.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-green-500 mr-2">•</span>
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-800 py-2 px-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <span className="text-sm text-slate-300">{sample.title}</span>
                    <div></div>
                  </div>
                  <div className="h-[500px] overflow-hidden relative">
                    <iframe 
                      src={`/demos/${sample.filename}`}
                      title={sample.title}
                      className="w-full h-full border-0"
                      sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-presentation"
                    ></iframe>
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Create Your Own Designs?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Start transforming your ideas into interactive, beautiful designs with 
            just a few clicks. No design skills required.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/signup" 
              className="px-6 py-3 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Get Started Free
            </Link>
            <Link
              to="/features"
              className="px-6 py-3 bg-white border border-gray-200 text-slate-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center"
            >
              Learn More About Features <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      {activeModalId && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <div className="w-full h-full flex flex-col">
            <div className="bg-slate-800 py-2 px-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-sm text-slate-300">
                {demoSamples.find(s => s.id === activeModalId)?.title}
              </span>
              <button 
                onClick={closeModal}
                className="text-slate-300 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 bg-white overflow-hidden">
              <iframe 
                src={`/demos/${demoSamples.find(s => s.id === activeModalId)?.filename}`}
                title={demoSamples.find(s => s.id === activeModalId)?.title}
                className="w-full h-full border-0"
                sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-presentation"
              ></iframe>
            </div>
          </div>
        </div>
      )}

      <MainFooter />
    </div>
  );
};

export default Demo;